import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useInvestments, useCreateInvestment, useUpdateInvestment, useDeleteInvestment, type InvestmentAccount } from '@/hooks/useInvestments';
import { useInvestmentTypes } from '@/hooks/useInvestmentTypes';
import { useFetchAccounts } from '@accountHooks';
import { useRiskRatings } from '@/hooks/useRiskRatings';
import { useInvestmentCurrencyConversion } from '@/hooks/useInvestmentCurrencyConversion';
import { useCreateInvestmentTransaction } from '@/hooks/useInvestmentTransactions';
import { useMemo, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { 
  PageHeaderSkeleton, 
  PortfolioSummarySkeleton, 
  DistributionChartSkeleton,
  AccountCardSkeleton 
} from '@/components/LoadingSkeletons';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ArrowDownToLine, ArrowUpFromLine, Plus, AlertCircle, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { TypeSummaryCard } from '@/components/investment-summary/TypeSummaryCard';
import { InvestmentDistributionChart } from '@/components/investment-charts/InvestmentDistributionChart';
import { InvestmentCard } from '@/components/investment-list/InvestmentCard';
import { CurrencyToggle } from '@/components/currency-toggle/CurrencyToggle';
import { InvestmentForm } from '@/components/investment-form/InvestmentForm';
import { InvestmentTransactionForm } from '@/components/investment-transaction-form/InvestmentTransactionForm';
import type { InvestmentFormData } from '@/hooks/useInvestments';
import type { InvestmentTransactionFormData } from '@/components/investment-transaction-form/InvestmentTransactionForm';

/**
 * FixedIncomeFundsPage component displays and manages Fixed Income Fund investments.
 * This is a dedicated page for Fixed Income Funds accessible at /investments/fixed-income-funds.
 */
export default function FixedIncomeFundsPage() {
  // Dialog state
  const [investmentFormOpen, setInvestmentFormOpen] = useState(false);
  const [transactionFormOpen, setTransactionFormOpen] = useState(false);
  const [transactionType, setTransactionType] = useState<'deposit' | 'withdrawal'>('deposit');
  const [editingInvestment, setEditingInvestment] = useState<InvestmentAccount | null>(null);
  const [deletingInvestmentId, setDeletingInvestmentId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch all investments
  const { 
    data: investments = [], 
    isLoading: investmentsLoading, 
    error: investmentsError 
  } = useInvestments();

  // Fetch investment types to get Fixed Income Fund type ID
  const { 
    data: investmentTypes = [], 
    isLoading: typesLoading, 
    error: typesError 
  } = useInvestmentTypes();

  // Fetch risk ratings for the investment form
  const { data: riskRatings = [], isError: riskRatingsError } = useRiskRatings();

  // Fetch accounts for transaction form
  const { 
    data: accounts = [], 
    isLoading: accountsLoading 
  } = useFetchAccounts();

  // Mutation hooks
  const createInvestment = useCreateInvestment();
  const updateInvestment = useUpdateInvestment();
  const deleteInvestment = useDeleteInvestment();
  const createInvestmentTransaction = useCreateInvestmentTransaction();

  // Get Fixed Income Fund type ID
  const fixedIncomeFundsTypeId = useMemo(() => {
    return investmentTypes.find(type => type.name === 'Fixed Income Fund')?.id;
  }, [investmentTypes]);

  const fixedIncomeFunds = useMemo(() => {
    if (!fixedIncomeFundsTypeId) return [];
    let filtered = investments.filter(inv => inv.investment_type_id === fixedIncomeFundsTypeId);
    
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(inv => 
        inv.fund_name?.toLowerCase().includes(lowerQuery) ||
        inv.fund_manager?.toLowerCase().includes(lowerQuery) ||
        inv.account_number?.toLowerCase().includes(lowerQuery) ||
        inv.member_number?.toLowerCase().includes(lowerQuery)
      );
    }
    
    return filtered;
  }, [investments, fixedIncomeFundsTypeId, searchQuery]);

  // Get base currency from first investment or default to USD
  const baseCurrency = useMemo(() => {
    return fixedIncomeFunds[0]?.currency || 'USD';
  }, [fixedIncomeFunds]);

  // Currency conversion hook
  const {
    selectedCurrency,
    setSelectedCurrency,
    convertedInvestments,
    totalNetWorth,
    isConverting,
    conversionError,
  } = useInvestmentCurrencyConversion({
    baseCurrency,
    investments: fixedIncomeFunds,
  });

  // Handle investment data loading errors
  useEffect(() => {
    if (investmentsError) {
      toast.error('Failed to load investments. Please try again.');
    }
  }, [investmentsError]);

  // Handle investment types loading errors
  useEffect(() => {
    if (typesError) {
      toast.error('Failed to load investment types. Please try again.');
    }
  }, [typesError]);

  // Combined loading state
  const isLoading = investmentsLoading || typesLoading || accountsLoading;

  // Handler functions for action buttons
  const handleAddInvestment = () => {
    setEditingInvestment(null);
    setInvestmentFormOpen(true);
  };

  const handleDeposit = () => {
    setTransactionType('deposit');
    setTransactionFormOpen(true);
  };

  const handleWithdraw = () => {
    setTransactionType('withdrawal');
    setTransactionFormOpen(true);
  };

  const handleEditInvestment = (investment: InvestmentAccount) => {
    setEditingInvestment(investment);
    setInvestmentFormOpen(true);
  };

  const handleDeleteInvestment = (investmentId: string) => {
    setDeletingInvestmentId(investmentId);
  };

  // Handler for saving investment (create or update)
  const handleSaveInvestment = async (data: InvestmentFormData) => {
    try {
      if (editingInvestment) {
        // Update existing investment
        await updateInvestment.mutateAsync({
          id: editingInvestment.id,
          ...data,
        });
        toast.success('Investment updated successfully');
      } else {
        // Create new investment
        await createInvestment.mutateAsync(data);
        toast.success('Investment created successfully');
      }
      setInvestmentFormOpen(false);
      setEditingInvestment(null);
    } catch (error) {
      console.error('Error saving investment:', error);
      toast.error(editingInvestment ? 'Failed to update investment' : 'Failed to create investment');
    }
  };

  // Handler for canceling investment form
  const handleCancelInvestmentForm = () => {
    setInvestmentFormOpen(false);
    setEditingInvestment(null);
  };

  // Handler for saving transaction (deposit or withdrawal)
  const handleSaveTransaction = async (data: InvestmentTransactionFormData) => {
    try {
      await createInvestmentTransaction.mutateAsync(data);
      toast.success(`${data.transaction_type === 'deposit' ? 'Deposit' : 'Withdrawal'} recorded successfully`);
      setTransactionFormOpen(false);
    } catch (error) {
      console.error('Error recording transaction:', error);
      toast.error(`Failed to record ${data.transaction_type}`);
    }
  };

  // Handler for canceling transaction form
  const handleCancelTransactionForm = () => {
    setTransactionFormOpen(false);
  };

  // Handler for confirming delete
  const handleConfirmDelete = async () => {
    if (!deletingInvestmentId) return;
    
    try {
      await deleteInvestment.mutateAsync(deletingInvestmentId);
      toast.success('Investment deleted successfully');
      setDeletingInvestmentId(null);
    } catch (error) {
      console.error('Error deleting investment:', error);
      toast.error('Failed to delete investment');
    }
  };

  // Handler for canceling delete
  const handleCancelDelete = () => {
    setDeletingInvestmentId(null);
  };

  // Disable deposit/withdraw buttons when no investments exist
  const hasInvestments = fixedIncomeFunds.length > 0;

  // Show loading skeletons while data is loading
  if (isLoading) {
    return (
      <ErrorBoundary
        onReset={() => {
          // Reset state and refetch data when error boundary resets
          window.location.reload();
        }}
      >
        <div className="space-y-6" aria-busy="true" aria-live="polite">
          <PageHeaderSkeleton />
          <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2">
            <PortfolioSummarySkeleton />
            <DistributionChartSkeleton />
          </div>
          <div className="space-y-4">
            <AccountCardSkeleton />
            <AccountCardSkeleton />
            <AccountCardSkeleton />
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary
      onReset={() => {
        // Reset state and refetch data when error boundary resets
        window.location.reload();
      }}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Fixed Income Funds</h1>
            <p className="text-sm text-muted-foreground sm:text-base">
              Manage your fixed income fund investments
            </p>
          </div>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:flex-wrap w-full lg:w-auto" role="group" aria-label="Fixed Income Funds actions">
            <div className="relative w-full md:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search funds..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex gap-2 w-full md:w-auto">
              <CurrencyToggle
                baseCurrency={baseCurrency}
                selectedCurrency={selectedCurrency}
                onCurrencyChange={setSelectedCurrency}
                balance={totalNetWorth}
                showBalance={false}
              />
              <Button
                onClick={handleDeposit}
                disabled={!hasInvestments}
                variant="outline"
                aria-label="Deposit funds to Fixed Income Fund"
                aria-disabled={!hasInvestments}
                className="min-h-[44px]"
              >
                <ArrowDownToLine className="mr-2 h-4 w-4" aria-hidden="true" />
                Deposit
              </Button>
              <Button
                onClick={handleWithdraw}
                disabled={!hasInvestments}
                variant="outline"
                aria-label="Withdraw funds from Fixed Income Fund"
                aria-disabled={!hasInvestments}
                className="min-h-[44px]"
              >
                <ArrowUpFromLine className="mr-2 h-4 w-4" aria-hidden="true" />
                Withdraw
              </Button>
              <Button
                onClick={handleAddInvestment}
                aria-label="Add new Fixed Income Fund investment"
                className="min-h-[44px]"
              >
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Add
              </Button>
            </div>
          </div>
        </div>

        {/* Currency Conversion Loading Indicator */}
        {isConverting && (
          <Alert role="status" aria-live="polite" aria-busy="true">
            <AlertDescription>
              Converting to {selectedCurrency}...
            </AlertDescription>
          </Alert>
        )}

        {/* Currency Conversion Error */}
        {conversionError && (
          <Alert variant="destructive" role="alert" aria-live="assertive">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>
              Failed to convert currencies. Displaying amounts in original currencies.
            </AlertDescription>
          </Alert>
        )}

        {/* Summary Cards and Distribution Chart */}
        <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2">
          <TypeSummaryCard
            typeId={fixedIncomeFundsTypeId || ''}
            investments={investments}
            currency={selectedCurrency}
            isLoading={isLoading}
          />
          <InvestmentDistributionChart
            investments={convertedInvestments}
            currency={selectedCurrency}
            isLoading={isLoading}
          />
        </div>

        {/* Investment Cards List or Empty State */}
        {hasInvestments ? (
          <div 
            className="space-y-4" 
            role="list" 
            aria-label="Fixed Income Fund investments"
            aria-live="polite"
          >
            {convertedInvestments.map((investment) => (
              <InvestmentCard
                key={investment.id}
                investment={investment}
                currency={selectedCurrency}
                onEdit={() => handleEditInvestment(investment)}
                onDelete={() => handleDeleteInvestment(investment.id)}
              />
            ))}
          </div>
        ) : (
          <Card role="status" aria-label="Empty state">
            <CardContent className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <p className="text-base sm:text-lg text-muted-foreground mb-4">
                No Fixed Income Fund investments yet
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground mb-6">
                Get started by adding your first Fixed Income Fund investment
              </p>
              <Button 
                onClick={handleAddInvestment} 
                className="min-h-[44px] w-full md:w-auto"
                aria-label="Add your first Fixed Income Fund investment"
              >
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Add Investment
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Investment Dialog */}
        <Dialog open={investmentFormOpen} onOpenChange={setInvestmentFormOpen}>
          <DialogContent 
            className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw] sm:w-full"
            aria-describedby="investment-dialog-description"
          >
            <DialogHeader>
              <DialogTitle>
                {editingInvestment ? 'Edit Investment' : 'Add Fixed Income Fund Investment'}
              </DialogTitle>
              <DialogDescription id="investment-dialog-description">
                {editingInvestment 
                  ? 'Update the details of your Fixed Income Fund investment.' 
                  : 'Add a new Fixed Income Fund investment to your portfolio.'}
              </DialogDescription>
            </DialogHeader>
            <InvestmentForm
              investment={editingInvestment}
              preselectedType={fixedIncomeFundsTypeId}
              investmentTypes={investmentTypes}
              riskRatings={riskRatings}
              riskRatingsError={riskRatingsError}
              onSave={handleSaveInvestment}
              onCancel={handleCancelInvestmentForm}
              isSubmitting={createInvestment.isPending || updateInvestment.isPending}
            />
          </DialogContent>
        </Dialog>

        {/* Deposit/Withdraw Transaction Dialog */}
        <Dialog open={transactionFormOpen} onOpenChange={setTransactionFormOpen}>
          <DialogContent 
            className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw] sm:w-full"
            aria-describedby="transaction-dialog-description"
          >
            <DialogHeader>
              <DialogTitle>
                {transactionType === 'deposit' ? 'Deposit Funds' : 'Withdraw Funds'}
              </DialogTitle>
              <DialogDescription id="transaction-dialog-description">
                {transactionType === 'deposit' 
                  ? 'Add funds to your Fixed Income Fund investment.' 
                  : 'Withdraw funds from your Fixed Income Fund investment.'}
              </DialogDescription>
            </DialogHeader>
            <InvestmentTransactionForm
              transactionType={transactionType}
              investments={fixedIncomeFunds}
              accounts={accounts}
              onSave={handleSaveTransaction}
              onCancel={handleCancelTransactionForm}
              isSubmitting={createInvestmentTransaction.isPending}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deletingInvestmentId} onOpenChange={(open) => !open && handleCancelDelete()}>
          <AlertDialogContent 
            className="w-[95vw] sm:w-full sm:max-w-[500px]"
            aria-describedby="delete-dialog-description"
          >
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription id="delete-dialog-description">
                This action cannot be undone. This will permanently delete the investment account
                and all associated transaction history.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
              <AlertDialogCancel 
                onClick={handleCancelDelete} 
                className="min-h-[44px] w-full sm:w-auto"
                aria-label="Cancel deletion"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmDelete}
                className="min-h-[44px] w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
                aria-label="Confirm deletion of investment"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ErrorBoundary>
  );
}
